import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell, inputCls } from "@/components/AdminTools";
import {
  ClipboardList,
  CheckCircle2,
  XCircle,
  Rocket,
  RefreshCw,
  Clock,
  Building2,
  Users,
  AlertTriangle,
  Send,
  Search,
  ChevronDown,
} from "lucide-react";

const STATUS_META = {
  pending_approval: { label: "Pending review", tone: "bg-blue-100 text-blue-900", Icon: Clock },
  approved: { label: "Approved · vendor sign-off", tone: "bg-amber-100 text-amber-900", Icon: AlertTriangle },
  active: { label: "Deployed", tone: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2 },
  rejected: { label: "Rejected", tone: "bg-rose-100 text-rose-800", Icon: XCircle },
};

function _fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function _money(n) {
  if (typeof n !== "number") return "—";
  return `$${n.toFixed(0)}`;
}

export default function AdminProgramRequests() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState("pending_approval");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null); // program id with open detail panel
  const [detail, setDetail] = useState({}); // { [pid]: detail object }
  const [notes, setNotes] = useState({}); // { [pid]: textarea content }

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/program-requests");
      setRows(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const totals = useMemo(() => {
    const t = { all: rows.length, pending_approval: 0, approved: 0, active: 0, rejected: 0 };
    for (const r of rows) {
      t[r.status] = (t[r.status] || 0) + 1;
    }
    return t;
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows
      .filter((r) => filter === "all" || r.status === filter)
      .filter((r) => {
        if (!needle) return true;
        return (
          (r.name || "").toLowerCase().includes(needle) ||
          (r.clinic_name || "").toLowerCase().includes(needle) ||
          (r.category || "").toLowerCase().includes(needle)
        );
      });
  }, [rows, filter, search]);

  const expand = async (pid) => {
    if (expanded === pid) {
      setExpanded(null);
      return;
    }
    setExpanded(pid);
    if (detail[pid]) return;
    try {
      const r = await api.get(`/admin/program-requests/${pid}`);
      setDetail((d) => ({ ...d, [pid]: r.data }));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const refreshOne = async (pid) => {
    try {
      const r = await api.get(`/admin/program-requests/${pid}`);
      setDetail((d) => ({ ...d, [pid]: r.data }));
      // also refresh the row in the list view
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const decide = async (pid, action) => {
    setBusyId(pid);
    try {
      await api.post(`/admin/program-requests/${pid}/${action}`, {
        notes: notes[pid] || null,
      });
      toast.success(
        action === "approve"
          ? "Program approved — vendor tasks dispatched."
          : action === "reject"
            ? "Program returned to clinic."
            : "Program deployed.",
      );
      setNotes((n) => ({ ...n, [pid]: "" }));
      await refreshOne(pid);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <ToolPageShell
      eyebrow="Admin · Operations · Program Requests"
      title="Program approval queue"
      intro="Clinics submit cash-pay programs assembled from vendor service selections. Approve to fan out per-vendor tasks, then deploy once every vendor signs off."
      actions={
        <button
          onClick={load}
          data-testid="program-requests-refresh"
          className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-50"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      }
    >
      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
        <Kpi label="Total" value={totals.all} testid="program-kpi-total" />
        <Kpi label="Pending" value={totals.pending_approval} tone="blue" testid="program-kpi-pending" />
        <Kpi label="Approved · awaiting vendors" value={totals.approved} tone="amber" testid="program-kpi-approved" />
        <Kpi label="Deployed" value={totals.active} tone="emerald" testid="program-kpi-active" />
        <Kpi label="Rejected" value={totals.rejected} tone="rose" testid="program-kpi-rejected" />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by program, clinic, or category…"
            data-testid="program-requests-search"
            className={`${inputCls} pl-9`}
          />
        </div>
        {[
          { key: "pending_approval", label: `Pending · ${totals.pending_approval}` },
          { key: "approved", label: `Approved · ${totals.approved}` },
          { key: "active", label: `Deployed · ${totals.active}` },
          { key: "rejected", label: `Rejected · ${totals.rejected}` },
          { key: "all", label: `All · ${totals.all}` },
        ].map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            data-testid={`program-requests-filter-${f.key}`}
            className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors ${
              filter === f.key
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center text-slate-500 py-12">Loading approval queue…</div>
      ) : filtered.length === 0 ? (
        <div
          className="text-center text-slate-500 py-12 rounded-2xl border border-dashed border-slate-300 bg-white"
          data-testid="program-requests-empty"
        >
          {filter === "pending_approval"
            ? "No programs awaiting review. Clinic-submitted programs appear here."
            : "No programs match this filter."}
        </div>
      ) : (
        <div className="space-y-3" data-testid="program-requests-list">
          {filtered.map((p) => {
            const meta = STATUS_META[p.status] || STATUS_META.pending_approval;
            const StatusIcon = meta.Icon;
            const isOpen = expanded === p.id;
            const d = detail[p.id];
            const tasks = d?.vendor_tasks || [];
            const tasksApproved = tasks.filter((t) => t.status === "approved").length;
            const tasksRejected = tasks.filter((t) => t.status === "rejected").length;
            const tasksPending = tasks.filter((t) => t.status === "pending").length;
            const allApproved = tasks.length > 0 && tasksPending === 0 && tasksRejected === 0;

            return (
              <div
                key={p.id}
                data-testid={`program-request-${p.id}`}
                className="rounded-2xl border border-slate-200 bg-white"
              >
                <button
                  type="button"
                  onClick={() => expand(p.id)}
                  data-testid={`program-request-expand-${p.id}`}
                  className="w-full text-left px-4 py-4 flex items-start gap-3 hover:bg-slate-50/60 rounded-2xl"
                >
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 shrink-0 mt-0.5 ${meta.tone}`}
                  >
                    <StatusIcon className="w-3 h-3" />
                    {meta.label}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{p.name}</div>
                    <div className="mt-0.5 text-[11px] text-slate-500 inline-flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1">
                        <Building2 className="w-3 h-3 text-slate-400" />
                        {p.clinic_name}
                      </span>
                      <span className="inline-flex items-center gap-1 capitalize">
                        <ClipboardList className="w-3 h-3 text-slate-400" />
                        {p.category} · {(p.services || []).length} service
                        {(p.services || []).length === 1 ? "" : "s"}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-3 h-3 text-slate-400" />
                        Submitted {_fmt(p.submitted_at)}
                      </span>
                    </div>
                  </div>
                  <ChevronDown
                    className={`w-4 h-4 text-slate-400 transition-transform shrink-0 mt-1 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {isOpen && (
                  <div className="border-t border-slate-100 px-4 py-4 space-y-4" data-testid={`program-request-detail-${p.id}`}>
                    {!d ? (
                      <div className="text-xs text-slate-500">Loading details…</div>
                    ) : (
                      <>
                        <div className="grid sm:grid-cols-3 gap-3 text-xs">
                          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                              Pricing
                            </div>
                            <div className="font-bold text-slate-900 mt-0.5">{_money(p.price)}</div>
                            <div className="text-[10px] text-slate-500 capitalize mt-0.5">
                              {p.pricing_model?.replace(/_/g, " ")} · {p.cadence?.replace(/_/g, " ")}
                            </div>
                          </div>
                          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                              Submitted by
                            </div>
                            <div className="font-bold text-slate-900 mt-0.5">{d.clinic_name || p.clinic_name}</div>
                            {d.clinic_email && (
                              <a
                                href={`mailto:${d.clinic_email}`}
                                className="text-[10px] text-blue-700 hover:underline"
                              >
                                {d.clinic_email}
                              </a>
                            )}
                          </div>
                          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                              Vendor tasks
                            </div>
                            <div className="font-bold text-slate-900 mt-0.5">
                              {tasks.length === 0 ? "—" : `${tasksApproved}/${tasks.length} approved`}
                            </div>
                            <div className="text-[10px] text-slate-500 mt-0.5">
                              {tasksPending} pending · {tasksRejected} rejected
                            </div>
                          </div>
                        </div>

                        {p.description && (
                          <div className="text-xs text-slate-700 leading-relaxed">{p.description}</div>
                        )}

                        <div>
                          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">
                            Services requested ({(p.services || []).length})
                          </div>
                          <div className="space-y-1.5">
                            {(p.services || []).map((s, i) => (
                              <div
                                key={`${s.name || ""}-${s.vendor_name || ""}-${i}`}
                                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs flex items-center gap-2"
                              >
                                <span className="font-semibold text-slate-900">{s.name}</span>
                                {s.vendor_name && (
                                  <span className="inline-flex items-center gap-1 text-slate-500">
                                    <Building2 className="w-3 h-3 text-slate-400" /> {s.vendor_name}
                                  </span>
                                )}
                                <span className="ml-auto tabular-nums text-slate-700">
                                  {_money(s.retail_price)}{" "}
                                  <span className="text-slate-400">/ {_money(s.wholesale_cost)}</span>
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {tasks.length > 0 && (
                          <div>
                            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">
                              Vendor sign-off
                            </div>
                            <div className="space-y-1.5">
                              {tasks.map((t) => (
                                <div
                                  key={t.id}
                                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs flex items-center gap-2"
                                  data-testid={`vendor-task-row-${t.id}`}
                                >
                                  <Building2 className="w-3 h-3 text-slate-400" />
                                  <span className="font-semibold text-slate-900">{t.vendor_name}</span>
                                  <span className="text-slate-500">
                                    · {t.services.map((s) => s.name).join(", ")}
                                  </span>
                                  <span
                                    className={`ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${
                                      t.status === "approved"
                                        ? "bg-emerald-100 text-emerald-800"
                                        : t.status === "rejected"
                                          ? "bg-rose-100 text-rose-800"
                                          : "bg-slate-100 text-slate-700"
                                    }`}
                                  >
                                    {t.status === "approved" && <CheckCircle2 className="w-2.5 h-2.5" />}
                                    {t.status === "rejected" && <XCircle className="w-2.5 h-2.5" />}
                                    {t.status === "pending" && <Clock className="w-2.5 h-2.5" />}
                                    {t.status}
                                  </span>
                                </div>
                              ))}
                            </div>
                            {tasksRejected > 0 && (
                              <div className="mt-2 text-[11px] text-rose-700">
                                One or more vendors rejected — return the program to the clinic so they can
                                swap vendors before deploy.
                              </div>
                            )}
                          </div>
                        )}

                        {p.admin_notes && (
                          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-700">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-0.5">
                              Admin note
                            </div>
                            {p.admin_notes}
                          </div>
                        )}

                        {/* Action area depending on lifecycle stage */}
                        {p.status === "pending_approval" && (
                          <div className="space-y-2">
                            <textarea
                              rows={2}
                              value={notes[p.id] || ""}
                              onChange={(e) => setNotes((n) => ({ ...n, [p.id]: e.target.value }))}
                              placeholder="Optional admin note (visible to clinic on rejection)…"
                              data-testid={`program-request-notes-${p.id}`}
                              className={inputCls}
                            />
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => decide(p.id, "approve")}
                                disabled={busyId === p.id}
                                data-testid={`program-request-approve-${p.id}`}
                                className="inline-flex items-center gap-1.5 bg-emerald-600 text-white rounded-full px-4 py-2 text-xs font-semibold hover:bg-emerald-700 disabled:opacity-60"
                              >
                                <CheckCircle2 className="w-3.5 h-3.5" /> Approve & dispatch to vendors
                              </button>
                              <button
                                type="button"
                                onClick={() => decide(p.id, "reject")}
                                disabled={busyId === p.id}
                                data-testid={`program-request-reject-${p.id}`}
                                className="inline-flex items-center gap-1.5 bg-rose-50 text-rose-700 border border-rose-200 rounded-full px-4 py-2 text-xs font-semibold hover:bg-rose-100 disabled:opacity-60"
                              >
                                <XCircle className="w-3.5 h-3.5" /> Reject (return to clinic)
                              </button>
                            </div>
                          </div>
                        )}

                        {p.status === "approved" && (
                          <div className="space-y-2">
                            <div className="text-[12px] text-slate-700">
                              {allApproved ? (
                                <span className="inline-flex items-center gap-1.5 text-emerald-800 font-semibold">
                                  <CheckCircle2 className="w-3.5 h-3.5" /> All vendors signed off — ready to deploy.
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 text-amber-800">
                                  <Clock className="w-3.5 h-3.5" /> Waiting on{" "}
                                  {tasksPending} vendor{tasksPending === 1 ? "" : "s"} ·{" "}
                                  {tasksRejected} rejected.
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                disabled={!allApproved || busyId === p.id}
                                onClick={() => decide(p.id, "deploy")}
                                data-testid={`program-request-deploy-${p.id}`}
                                className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-xs font-semibold hover:bg-slate-800 disabled:opacity-50"
                              >
                                <Rocket className="w-3.5 h-3.5" /> Deploy to clinic
                              </button>
                              <button
                                type="button"
                                onClick={() => refreshOne(p.id)}
                                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900"
                              >
                                <RefreshCw className="w-3.5 h-3.5" /> Refresh vendor status
                              </button>
                            </div>
                          </div>
                        )}

                        {p.status === "active" && (
                          <div className="text-[12px] text-emerald-800 inline-flex items-center gap-1.5 font-semibold">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Deployed {_fmt(p.deployed_at)} · live in clinic portal.
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </ToolPageShell>
  );
}

function Kpi({ label, value, tone = "slate", testid }) {
  const TONE = {
    slate: "bg-white border-slate-200",
    blue: "bg-blue-50 border-blue-200",
    amber: "bg-amber-50 border-amber-200",
    emerald: "bg-emerald-50 border-emerald-200",
    rose: "bg-rose-50 border-rose-200",
  };
  return (
    <div data-testid={testid} className={`rounded-2xl border p-3 ${TONE[tone] || TONE.slate}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className="text-xl font-bold text-slate-900 tabular-nums leading-tight mt-0.5">{value}</div>
    </div>
  );
}
